import {Brick, ErrorFlow, ListDef, QueryResult, registerBrick} from 'olympe';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {tagToDisplayER} from '../tagTransforms';

/**
 * A present EPR ID on the JSON wins. Otherwise the ID is derived from the tag.
 *
 * @param {string} prJSON The purchase request JSON.
 * @param {string} prTag The Purchase request tag used when the JSON has no EPR ID.
 * @return {string} EPR ID to display for this purchase request.
 */
function resolveEPRID(prJSON, prTag) {
    let eprId = '';

    if (prJSON) {
        eprId = new PurchaseRequest(prJSON).EPRId;
    }

    return eprId || tagToDisplayER(prTag);
}

/**
 * Normalizes an Olympe list input into a plain array.
 * Missing inputs are treated as an empty list.
 *
 * @param {Array|null|undefined} collection
 * @return {Array}
 */
function toArray(collection) {
    if (collection == null) {
        return [];
    }
    if (Array.isArray(collection)) {
        return collection;
    }
    throw new Error('Expected a list of purchase requests');
}

export default class GetMultipleEPRIDs extends Brick {
    /**
     * Resolves one EPR ID per purchase request.
     * JSON values and tags are paired by index. The longer list sets the result length.
     *
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {string[]} pRJSONs Serialized purchase requests, aligned with `pRTags`.
     * @param {string[]} pRTags Purchase request tags, aligned with `pRJSONs`.
     * @param {function(string[])} setEPRIDs Receives one EPR ID per purchase request, in input order.
     * @param {function(ErrorFlow)} setErrorFlow Receives an ErrorFlow when an EPR ID cannot be read.
     */
    update($, [pRJSONs, pRTags], [setEPRIDs, setErrorFlow]) {
        try {
            const prJSONs = toArray(pRJSONs);
            const prTags = toArray(pRTags);
            const count = Math.max(prJSONs.length, prTags.length);
            const eprIds = [];

            for (let i = 0; i < count; i++) {
                eprIds.push(resolveEPRID(prJSONs[i], prTags[i]));
            }

            setEPRIDs(eprIds);
        } catch (e) {
            console.error(e);
            setErrorFlow(ErrorFlow.create('Cannot read multiple EPR IDs', 2074));
        }
    }
}

registerBrick('01a0f1a8c91387d240b8', GetMultipleEPRIDs);
